import React, { createContext, useContext, useState } from 'react';
import * as defaultValidators from '../utils/ValidationService';

// Create context
export const ValidationContext = createContext();

/**
 * ValidationProvider - Provides validation functions to components in the tree
 */
export const ValidationProvider = ({ children, customValidators = {} }) => {
  // Combine default validators with any custom validators
  const [validators, setValidators] = useState({
    ...defaultValidators,
    ...customValidators
  });

  // Add a new validator or update an existing one
  const addValidator = (name, validator) => {
    setValidators(prev => ({
      ...prev,
      [name]: validator
    }));
  };

  // Get a validator by name
  const getValidator = (name) => {
    return validators[name] || null;
  };

  // Helper to normalize a value using a specific validator
  const normalizeValue = (name, value) => {
    const validator = validators[name];
    if (validator && validator.normalize) {
      return validator.normalize(value);
    }
    return value;
  };

  // Helper to validate a form with multiple fields
  const validateForm = (formData, validationSchema) => {
    const errors = {};
    let isValid = true;

    Object.entries(validationSchema).forEach(([fieldName, validatorName]) => {
      const validator = validators[validatorName];
      if (validator && validator.validate) {
        const value = formData[fieldName] || '';
        const result = validator.validate(value);
        
        if (!result.isValid) {
          errors[fieldName] = result.message;
          isValid = false;
        }
      }
    });

    return { isValid, errors };
  };

  // Helper to normalize form data for API submission
  const normalizeFormData = (formData, validationSchema) => {
    const normalizedData = { ...formData };

    Object.entries(validationSchema).forEach(([fieldName, validatorName]) => {
      const validator = validators[validatorName];
      if (validator && validator.normalize && normalizedData[fieldName] !== undefined) {
        normalizedData[fieldName] = validator.normalize(normalizedData[fieldName]);
      }
    });

    return normalizedData;
  };

  const value = {
    validators,
    addValidator,
    getValidator,
    normalizeValue,
    validateForm,
    normalizeFormData
  };

  return (
    <ValidationContext.Provider value={value}>
      {children}
    </ValidationContext.Provider>
  );
};

// Custom hook to use validation
export const useValidation = () => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error('useValidation must be used within a ValidationProvider');
  }
  return context;
}; 